import {observer} from 'mobx-react-lite'
import React, {useEffect, useLayoutEffect} from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {Sync} from '@client/sync'
import {showConfirm} from '@components/common/base/notifications'
import {Popup} from '@components/common/base/popup'
import {
  CompanyInvitePopupContext,
  CompanyInvitePopupStore,
  useCompanyInvitePopupStore
} from '@components/dashboard/company-invite-popup/company-invite-popup-store'
import {RoleEdit} from '@components/project-participants/role-edit'
import {useMemoOne} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {compare} from './helpers'
import {InviteTabContent} from './invite-tab-content'
import {ProjectParticipantsFooter} from './project-participants-footer'
import {
  ProjectParticipantContext,
  ProjectParticipantsStore,
  useProjectParticipantStore
} from './project-participants-store'
import {ProjectParticipantsUnionProps} from './types'
import {UsersRolesTabContent} from './users-roles-tab-content'

export const ProjectParticipantsBase = (
  props: ProjectParticipantsUnionProps
) => {
  const store = useMemoOne(() => new ProjectParticipantsStore(props), [])
  const inviteStore = useMemoOne(
    () => new CompanyInvitePopupStore('manage-project'),
    []
  )

  useLayoutEffect(() => {
    store.syncProps(props)
  }, [props.project, props.save, props.close, props.roles, props.allUsers])

  useLayoutEffect(() => {
    inviteStore.syncProject(store.props.project)
    inviteStore.syncCommon()
  }, [store.props.project?.id, store.props.project?.data?.roles])

  return (
    <ProjectParticipantContext.Provider value={store}>
      <CompanyInvitePopupContext.Provider value={inviteStore}>
        <ProjectParticipants {...props} />
      </CompanyInvitePopupContext.Provider>
    </ProjectParticipantContext.Provider>
  )
}

const ProjectParticipants = observer((props: ProjectParticipantsUnionProps) => {
  const {type, project, allUsers: initialUsers} = props
  const {requestedNewRole} = props

  const [state, store] = useProjectParticipantStore()
  const [, inviteStore] = useCompanyInvitePopupStore()

  useEffect(() => {
    const box = Sync.getCurrentSandbox()
    const res = [...initialUsers]
    if (!state.sort) {
      res.sort(
        (a, b) =>
          compare('tick', true, state.chosenRoles, box)(a, b) ||
          compare('name', true, state.chosenRoles, box)(a, b)
      )
    }
    store.setAllUsers(res)
  }, [props.project, props.save, props.close, props.roles, props.allUsers])

  useEffect(() => {
    if (requestedNewRole) {
      store.createRole()
    }
  }, [requestedNewRole])

  useEffect(() => store.resetFilter(), [project?.id, state.tab])

  useEffect(() => {
    if (state.sort) {
      store.sortUsers()
    }
  }, [state.sort, state.sortAsc, state.chosenRoles])

  if (!!state.editedRole && store.rolesSupported) {
    return (
      <RoleEdit
        projectId={project?.id ?? store.newProjectId}
        roles={project?.data?.roles ?? state.newRoles}
        initialRole={state.editedRole}
        saveRole={store.saveRole}
        back={store.cancelEditRole}
        close={store.closeWithWarn}
        isNewProject={!project}
      />
    )
  }

  const bem = Util.bemFrom('project-participants')
  const isMainTabs = store.isUsersTab || store.isRolesTab

  return (
    <Popup
      close={() => {
        if (store.isInviteTab && inviteStore.emails.length) {
          void showConfirm(Lang.msg.proj.unsavedChangesHint).then(data => {
            data && store.closeWithWarn()
          })
        } else {
          store.closeWithWarn()
        }
      }}
      title={store.popupTitle}
      width={800}
      autoHeight={!isMainTabs}
      footer={<ProjectParticipantsFooter type={type} />}
      className={bem('popup')}
      noScroll={true}
      contentClassName={cn(isMainTabs && 'h-[calc(100%-68px-91px)]')}
      fixedPosition
    >
      {isMainTabs && <UsersRolesTabContent />}
      {store.isInviteTab && <InviteTabContent />}
    </Popup>
  )
})
